import React from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Building2, Zap, Clock, TriangleAlert, Pause, Play, Loader2 } from 'lucide-react';
import { clsx } from 'clsx';
import { pulseApi, type PulseAccount } from '../../services/pulse-api';
import { handleApiError } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface AccountCardProps {
  account: PulseAccount;
}

/**
 * One row of the Pulse account list. Active accounts link to the detail page;
 * paused accounts show a "Paused" badge + Resume action; eligible accounts link
 * to the activate flow; ineligible cards are faded with the reason shown inline.
 */
export const AccountCard: React.FC<AccountCardProps> = ({ account }) => {
  if (account.state === 'active') return <ActiveCard account={account} />;
  if (account.state === 'paused') return <PausedCard account={account} />;
  if (account.state === 'eligible') return <EligibleCard account={account} />;
  return <IneligibleCard account={account} />;
};

const ActiveCard: React.FC<{ account: PulseAccount }> = ({ account }) => {
  const exec = account.executionJob;
  return (
    <Link
      to={`/pulse/accounts/${account.accountId}`}
      className="block rounded-xl bg-white dark:bg-white/5 border border-border p-5 hover:border-primary transition-colors"
    >
      <div className="flex items-start justify-between gap-4">
        <AccountIdentity account={account} />
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-success bg-success/15 px-2.5 py-1 rounded-full shrink-0">
          <span className="w-1.5 h-1.5 rounded-full bg-success" /> Monitoring
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-border text-sm">
        <Stat label="Last run" value={formatTime(exec?.lastRun)} />
        <Stat label="Next run" value={formatTime(exec?.nextRun)} />
        <Stat label="Frequency" value={exec?.scheduleLabel ?? '—'} />
      </div>

      {exec?.lastError && (
        <div className="flex items-center gap-2 mt-3 text-xs text-warn bg-warn/15 border border-warn/20 rounded-lg px-3 py-2">
          <TriangleAlert className="w-3.5 h-3.5 shrink-0" />
          Last execution failed (<span className="font-mono">{exec.lastError}</span>) — Pulse will retry on the next run.
        </div>
      )}
    </Link>
  );
};

const PausedCard: React.FC<{ account: PulseAccount }> = ({ account }) => {
  const exec = account.executionJob;
  const queryClient = useQueryClient();
  const resumeMutation = useMutation({
    mutationFn: () => pulseApi.resume(account.accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pulse', 'accounts'] });
      queryClient.invalidateQueries({ queryKey: ['pulse', 'account', account.accountId] });
      toast.success('Monitoring resumed');
    },
    onError: (error: unknown) => {
      toast.error(handleApiError(error) || 'Failed to resume monitoring');
    },
  });
  // The card is a Link to the detail page; keep the Resume click from navigating.
  const onResume = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resumeMutation.mutate();
  };
  return (
    <Link
      to={`/pulse/accounts/${account.accountId}`}
      className="block rounded-xl bg-white dark:bg-white/5 border border-border p-5 hover:border-primary transition-colors"
    >
      <div className="flex items-start justify-between gap-4">
        <AccountIdentity account={account} />
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground bg-muted px-2.5 py-1 rounded-full shrink-0">
          <Pause className="w-3 h-3" /> Paused
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-border text-sm">
        <Stat label="Last run" value={formatTime(exec?.lastRun)} />
        <Stat label="Next run" value="Paused" />
        <Stat label="Frequency" value={exec?.scheduleLabel ?? '—'} />
      </div>

      <div className="mt-3 flex justify-end">
        <button
          onClick={onResume}
          disabled={resumeMutation.isPending}
          className="inline-flex items-center gap-1.5 text-sm font-medium bg-primary text-white rounded-lg px-3 py-1.5 hover:bg-primary transition-colors disabled:opacity-50"
        >
          {resumeMutation.isPending ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Play className="w-3.5 h-3.5" />
          )}
          Resume
        </button>
      </div>
    </Link>
  );
};

const EligibleCard: React.FC<{ account: PulseAccount }> = ({ account }) => {
  const hasAnalysis = !!account.definitionJob;
  return (
    <div className="rounded-xl bg-white dark:bg-white/5 border border-border p-5">
      <AccountIdentity account={account} />
      <Link
        to={`/pulse/accounts/${account.accountId}/activate`}
        className="mt-4 w-full inline-flex items-center justify-center gap-1.5 text-sm font-medium bg-primary text-white rounded-lg px-3 py-2 hover:bg-primary transition-colors"
      >
        <Zap className="w-4 h-4" />
        {hasAnalysis ? 'Continue setup' : 'Activate monitoring'}
      </Link>
    </div>
  );
};

const IneligibleCard: React.FC<{ account: PulseAccount }> = ({ account }) => (
  <div className="rounded-xl bg-white dark:bg-white/5 border border-border p-5 opacity-60 cursor-not-allowed select-none">
    <AccountIdentity account={account} />
    <div className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
      <Clock className="w-3.5 h-3.5" />
      {account.ineligibleReason === 'not_ingestion_type'
        ? "This integration type isn't supported for monitoring yet."
        : 'Syncs too infrequently — needs to sync at least every 2 hours.'}
    </div>
  </div>
);

/**
 * Icon + account name + source line. The list spans every business in the
 * workspace (R11), so the owning business leads the source line — but only when
 * there is more than one, where it actually disambiguates.
 */
const AccountIdentity: React.FC<{ account: PulseAccount }> = ({ account }) => {
  const { organizations } = useAuth();
  const showBusiness = organizations.length > 1;
  return (
    <div className="flex items-start gap-3 min-w-0">
      <div className="w-10 h-10 rounded-lg bg-muted grid place-items-center shrink-0">
        <Building2 className="w-5 h-5 text-muted-foreground" />
      </div>
      <div className="min-w-0">
        <div className="font-semibold text-foreground truncate">{account.accountName}</div>
        <div className="text-sm text-muted-foreground truncate">
          {showBusiness && (
            <span className="text-foreground/70">{account.organizationName} · </span>
          )}
          {account.dataSourceName} · syncs {account.syncFrequencyLabel}
        </div>
      </div>
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className={clsx('font-medium mt-0.5 text-foreground')}>{value}</div>
  </div>
);

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
